// Miroir des jours travaillés côté backend (server.py : employee_works_on,
// _french_public_holidays, is_french_public_holiday) — utilisé partout où le
// front doit savoir si un salarié travaille un jour donné (planning, congés).

// Index 0 = dimanche pour matcher Date.getDay() (0 = dimanche ... 6 = samedi)
export const WEEKDAYS_BY_GETDAY = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

// Miroir de _easter_sunday (backend) : algorithme de Meeus/Jones/Butcher.
export const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 1-indexé
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

const frenchHolidaysCache = {};

// Miroir de _french_public_holidays (backend).
export const frenchPublicHolidaysForYear = (year) => {
  if (!frenchHolidaysCache[year]) {
    const easter = easterSunday(year);
    const addDays = (date, n) => {
      const d = new Date(date);
      d.setDate(d.getDate() + n);
      return d;
    };
    const dates = [
      new Date(year, 0, 1),   // Jour de l'an
      new Date(year, 4, 1),   // Fête du travail
      new Date(year, 4, 8),   // Victoire 1945
      new Date(year, 6, 14),  // Fête nationale
      new Date(year, 7, 15),  // Assomption
      new Date(year, 10, 1),  // Toussaint
      new Date(year, 10, 11), // Armistice
      new Date(year, 11, 25), // Noël
      addDays(easter, 1),     // Lundi de Pâques
      addDays(easter, 39),    // Ascension
      addDays(easter, 50),    // Lundi de Pentecôte
    ];
    frenchHolidaysCache[year] = new Set(dates.map(d => d.toDateString()));
  }
  return frenchHolidaysCache[year];
};

export const isFrenchPublicHoliday = (day) => frenchPublicHolidaysForYear(day.getFullYear()).has(day.toDateString());

// Jour ouvré standard : du lundi au vendredi, hors jours fériés — le rythme d'un
// chantier qui n'a pas encore d'équipe.
export const isStandardWorkday = (day) => day.getDay() >= 1 && day.getDay() <= 5 && !isFrenchPublicHoliday(day);

const atMidnight = (value) => {
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (isNaN(d)) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

// Miroir de employee_is_absent_on (backend) : congés, arrêts maladie, formations.
export const employeeIsAbsentOn = (employee, day) => {
  const dayTime = atMidnight(day)?.getTime();
  for (const field of ['specificLeaveDates', 'sickLeaveDates']) {
    for (const leave of employee[field] || []) {
      if (atMidnight(leave)?.getTime() === dayTime) return true;
    }
  }
  for (const item of employee.otherAbsences || []) {
    if (atMidnight(item?.date)?.getTime() === dayTime) return true;
  }
  for (const week of employee.trainingWeeks || []) {
    const start = week && week.start ? atMidnight(week.start) : null;
    const end = week && week.end ? atMidnight(week.end) : null;
    if (start && end && dayTime >= start.getTime() && dayTime <= end.getTime()) return true;
  }
  return false;
};

// Date "YYYY-MM-DD" lue en heure LOCALE : new Date("2026-08-31") serait
// interprété en UTC et pourrait basculer sur la veille selon le fuseau.
const parseLocalDay = (value) => {
  if (!value) return null;
  if (value instanceof Date) return atMidnight(value);
  const m = String(value).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return atMidnight(value);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

// Bornes de contrat : avant l'embauche et après la fin de contrat, le salarié
// n'est pas disponible — exactement comme le fait `employee_works_on` côté
// serveur. Sans ça, l'app comptait des jours d'absence (et calculait des fins
// de chantier) pour quelqu'un qui n'est plus, ou pas encore, dans l'effectif.
export const employeeUnderContractOn = (employee, day) => {
  const d = atMidnight(day);
  if (!d) return false;
  const start = parseLocalDay(employee.contractStartDate);
  if (start && d < start) return false;
  const end = parseLocalDay(employee.contractEndDate);
  if (end && d > end) return false;
  return true;
};

export const employeeWorksOnDay = (employee, day) => {
  if (isFrenchPublicHoliday(day)) return false;
  if (!employeeUnderContractOn(employee, day)) return false;
  const workDays = employee.workDays && employee.workDays.length > 0
    ? employee.workDays
    : ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'];
  if (!workDays.includes(WEEKDAYS_BY_GETDAY[day.getDay()])) return false;
  return !employeeIsAbsentOn(employee, day);
};

// Jour théoriquement travaillé (jour de semaine travaillé, hors férié), SANS
// regarder les absences — sert à compter les congés : un congé posé un
// week-end ou un férié ne consomme pas de jour de congé.
export const employeeNominalWorkday = (employee, day) => {
  const d = atMidnight(day);
  if (!d || isFrenchPublicHoliday(d)) return false;
  const workDays = employee.workDays && employee.workDays.length > 0
    ? employee.workDays
    : ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'];
  return workDays.includes(WEEKDAYS_BY_GETDAY[d.getDay()]);
};

// Nombre de jours d'absence qui comptent réellement (jours théoriquement
// travaillés uniquement — week-ends et fériés exclus du décompte).
export const countEffectiveLeaveDays = (employee, dates) =>
  (dates || []).filter(d => employeeNominalWorkday(employee, d)).length;

// Miroir de compute_real_end_date (backend) : durationInDays est un nombre de jours
// TRAVAILLÉS, pas de jours calendaires — on saute donc les jours où personne de
// l'équipe assignée ne travaille (week-ends, etc.) pour trouver la vraie fin.
export const computeRealEndDate = (startDate, durationInDays, team) => {
  if (!durationInDays || durationInDays <= 0) return startDate;
  if (!team || team.length === 0) {
    const fallback = new Date(startDate);
    fallback.setDate(startDate.getDate() + durationInDays - 1);
    return fallback;
  }

  let workedDays = 0;
  let current = new Date(startDate);
  const maxIterations = durationInDays * 3 + 60; // garde-fou
  for (let i = 0; i < maxIterations; i++) {
    if (team.some(emp => employeeWorksOnDay(emp, current))) {
      workedDays++;
      if (workedDays >= durationInDays) return current;
    }
    current = new Date(current);
    current.setDate(current.getDate() + 1);
  }
  return current;
};

// Liste tous les jours ouvrés (travaillés par ce salarié) entre from et to inclus.
export const workingDaysInRange = (employee, from, to) => {
  const days = [];
  let current = new Date(from);
  current.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  while (current <= end) {
    if (employeeWorksOnDay(employee, current)) {
      days.push(new Date(current));
    }
    current = new Date(current);
    current.setDate(current.getDate() + 1);
  }
  return days;
};
